import type { FileSystemTree, WebContainerProcess } from "@webcontainer/api";

import { useCallback, useEffect, useRef, useState } from "react";

import { isDevelopment } from "@/editor/utils/env";
import { transformFileTree } from "@/features/react-editor/utils/filesUtils";
import { WebContainer } from "@webcontainer/api";

// Constants
const EXPECTED_PORT = 3001;

const TERMINAL_ENV = {
  NO_COLOR: "1", // Standard opt-out for colored output
  FORCE_COLOR: "0", // Ensures tools like chalk suppress color
  TERM: "dumb", // Prevents terminal formatting
} as const;

type LogMessage = {
  date: Date;
  message: string;
};

type UseWebContainerParams = {
  fileTree?: FileSystemTree;
};
type PageStatusRecord = {
  timestamp: number;
  status: number;
};
type PageStatusMap = Record<string, PageStatusRecord>;
type UseWebContainerReturn = {
  logs: LogMessage[];
  webContainerUrl: string | undefined;
  /**
   * A map of page routes to their latest status resulting from a GET request.
   * This is useful for seeing what pages have been rendered and what their
   * status was when they were rendered. Statuses are 200, 404, 500, etc.
   */
  pageStatusMap: PageStatusMap;
  mountAdditionalFiles:
    | ((fileTree: FileSystemTree) => Promise<void>)
    | undefined;
  fs: WebContainer["fs"] | undefined;
  error: any | null;
  restart: () => void;
};

export function useWebContainerHook({
  fileTree,
}: UseWebContainerParams): UseWebContainerReturn {
  const [webContainer, setWebContainer] = useState<WebContainer | undefined>(
    undefined,
  );
  const [isNextServerReady, setIsNextServerReady] = useState(false);
  const [error, setError] = useState<any | null>(null);
  const [webContainerUrl, setWebContainerUrl] = useState<string | undefined>(
    undefined,
  );

  const [pageStatusMap, setPageStatusMap] = useState<PageStatusMap>({});

  const devProcessRef = useRef<WebContainerProcess | null>(null);

  const [logs, setLogs] = useState<LogMessage[]>([]);

  const addLogMessage = useCallback((message: string) => {
    if (message.includes("✓ Ready in")) {
      setIsNextServerReady(true);
    }
    const trimmedMessage = message.trim();
    if (trimmedMessage.startsWith("GET")) {
      const [, pageRoute, status] = trimmedMessage.split(" ");
      const statusNumber = Number(status);
      if (!pageRoute || isNaN(statusNumber)) {
        return;
      }
      setPageStatusMap((prev) => ({
        ...prev,
        [pageRoute]: {
          timestamp: Date.now(),
          status: statusNumber,
        },
      }));
    }
    setLogs((prev) => [...prev, { date: new Date(), message }]);
  }, []);

  // Effect 1: Boot WebContainer and set up listeners ONCE, and clean up on
  // unmount
  useEffect(() => {
    let webContainerInstance: WebContainer | undefined;
    async function bootWebContainer() {
      try {
        addLogMessage("Booting WebContainer...");
        webContainerInstance = await WebContainer.boot({
          // NOTE (Gabe 2025-06-24): In development we use the COOP & COEP
          // headers to enable WebContainers (in vite.config). We can't use
          // these in prod because they break OAuth, and because our app is
          // client-side, the entire thing shares the same headers. in order to
          // avoid splitting the oauth flow, we use Chrome's Origin Trial
          // (https://developer.chrome.com/origintrials/#/view_trial/303992974847508481)
          // to enable SharedArrayBuffer which requires passing coep: "none"
          // here. More info:
          // https://blog.stackblitz.com/posts/cross-browser-with-coop-coep/
          ...(isDevelopment
            ? {}
            : {
                coep: "none",
              }),
        });

        webContainerInstance.on("error", (error) => {
          addLogMessage(`WebContainer error: ${error.message}`);
        });

        webContainerInstance.on("server-ready", (port, url) => {
          addLogMessage(`Server ready at ${url}`);

          if (port !== EXPECTED_PORT) {
            return;
          }

          setWebContainerUrl(url);
        });

        setWebContainer(webContainerInstance);

        addLogMessage("WebContainer booted successfully.");
      } catch (error) {
        addLogMessage(
          `Error booting WebContainer: ${error instanceof Error ? error.message : String(error)}`,
        );
        setError(error);
        setWebContainer(undefined);
      }
    }

    void bootWebContainer();

    return () => {
      if (webContainerInstance) {
        try {
          webContainerInstance.teardown();
        } catch (error) {
          addLogMessage(
            `Error during WebContainer teardown: ${error instanceof Error ? error.message : String(error)}`,
          );
          setError(error);
        }
        setWebContainer(undefined);
      }
    };
  }, [addLogMessage]);

  const mountFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!fileTree || !webContainer) {
        return;
      }

      try {
        addLogMessage(
          "Transforming TypeScript/TSX files with source metadata...",
        );
        const transformedFileTree = transformFileTree({
          fileTree,
          matchPatterns: [".ts", ".tsx"],
        });

        addLogMessage("Mounting files...");
        await webContainer.mount(transformedFileTree);
        addLogMessage("Files mounted successfully.");
      } catch (error) {
        console.error("Failed to mount files:", error);
        addLogMessage(
          `Error mounting files: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    [addLogMessage, webContainer],
  );
  const setupProject = useCallback(async () => {
    if (!fileTree || !webContainer) {
      return;
    }
    if (devProcessRef.current) {
      addLogMessage("Killing dev process...");
      setWebContainerUrl(undefined);
      devProcessRef.current.kill();
      devProcessRef.current = null;
    }

    try {
      await mountFiles(fileTree);

      addLogMessage("Running npm install...");
      const installProcess = await webContainer.spawn("npm", ["install"], {
        env: TERMINAL_ENV,
      });
      void installProcess.output.pipeTo(
        new WritableStream({
          write(data) {
            addLogMessage(data);
          },
        }),
      );
      const installExitCode = await installProcess.exit;
      if (installExitCode !== 0) {
        addLogMessage(`npm install failed with exit code: ${installExitCode}`);
        return;
      }
      addLogMessage("npm install completed.");

      addLogMessage("Starting development server...");
      devProcessRef.current = await webContainer.spawn("npm", ["run", "dev"], {
        env: TERMINAL_ENV,
      });
      void devProcessRef.current.output.pipeTo(
        new WritableStream({
          write(data) {
            addLogMessage(data);
          },
        }),
      );
    } catch (error) {
      console.error("Failed to setup project:", error);
      addLogMessage(
        `Error setting up project: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [fileTree, addLogMessage, mountFiles, webContainer]);

  // Effect 2: Handle initial setup (npm install + dev server) and file mounting
  useEffect(() => {
    if (!fileTree) {
      return;
    }

    if (!devProcessRef.current) {
      // Only run npm install and start dev server if this is the initial setup
      void setupProject();
    } else {
      addLogMessage("Files updated - dev server will hot reload");
      void mountFiles(fileTree);
    }
  }, [setupProject, fileTree, mountFiles, addLogMessage]);

  const mountAdditionalFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!webContainer) {
        addLogMessage(
          "Cannot mount additional files - WebContainer not booted",
        );
        return;
      }

      addLogMessage("Mounting additional files...");

      // NOTE (Bobby 2025-07-04): We need to transform file tree on mountAdditionalFiles because when chat/llm makes a change,
      // it'll return the file tree with the changes, but the file tree doesn't have the source metadata.
      addLogMessage(
        "Transforming TypeScript/TSX files with source metadata...",
      );
      const transformedFileTree = transformFileTree({
        fileTree,
        matchPatterns: [".ts", ".tsx"],
      });

      return webContainer.mount(transformedFileTree);
    },
    [addLogMessage, webContainer],
  );

  return {
    logs,
    webContainerUrl: isNextServerReady ? webContainerUrl : undefined,
    pageStatusMap,
    fs: webContainer?.fs,
    mountAdditionalFiles,
    error,
    restart: () => void setupProject(),
  };
}

export function detectBuildError(message: string): boolean {
  const errorPatterns = [
    // Next.js compilation errors
    /failed to compile/i,
    /compilation error/i,
    /module not found/i,
    /cannot resolve module/i,
    /syntaxerror/i,
    /typeerror.*at compile time/i,

    // TypeScript errors
    /type error/i,
    /TS\d{4}:/,

    // React/JSX errors
    /jsx element .* has no corresponding closing tag/i,
    /expected corresponding jsx closing tag/i,

    // Import/Export errors
    /module '".*"' has no exported member/i,
    /cannot find module/i,

    // Build process failures
    /npm err!/i,
    /build failed/i,
    /error occurred prerendering page/i,

    // Next.js specific errors
    /error: element type is invalid/i,
    /unhandled runtime error/i,
  ];

  return errorPatterns.some((pattern) => pattern.test(message));
}

export function detectFileStatusError(message: string): boolean {
  // HTTP error status codes that indicate file/server errors
  const errorStatusCodes = ["404", "500", "502", "503", "504"];

  const httpRequestPattern = /(get)\s+\/\S+\s+(\d{3})\s+in\s+\d+ms/i;
  const match = message.match(httpRequestPattern);

  if (match) {
    const statusCode = match[2];
    return errorStatusCodes.includes(statusCode ?? "");
  }

  return false;
}

export function detect200StatusCode(message: string): boolean {
  const httpRequestPattern = /(get)\s+\/\S+\s+(\d{3})\s+in\s+\d+ms/i;
  const match = message.match(httpRequestPattern);

  if (match) {
    const statusCode = match[2];
    return statusCode === "200";
  }

  return false;
}
